import React, { useState, useEffect } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { useInvoice } from '../contexts/InvoiceContext';
import { useAccount } from '../contexts/AccountContext';
import { ordersApi, customersApi, productApi, orgApi, accountsApi } from '../api';
import { invoiceImageService } from '../services/InvoiceImageService';
import {
  buildPrintableInvoiceFromDraft,
  buildPrintableInvoiceFromOrder,
  printableInvoiceToLegacyInvoiceData,
  extractBusinessFromOrg,
} from '../services/printer';
import { usePrinterService } from '../hooks/usePrinterService';
import { useOfflineOperations } from '../hooks/useOfflineOperations';
import { useOfflineCollection } from '../hooks/useOfflineCollection';
import { ENTITY_TYPES } from '../db';
import { dataService } from '../services/dataService';
import { syncService } from '../services/sync/syncService';
import PermissionAction from './PermissionAction';
import { showToast } from '../utils/toast';

const FastBilling = ({ user, subStatus, onSubscribe, orderToEdit, onEditComplete }) => {
  const { hasPermission } = usePermissions();
  const { selectedAccountId } = useAccount();
  const { canPerformOperation, showOfflineWarning, message: offlineMessage } = useOfflineOperations('billing');
  const { currentInvoiceNumber, generateNextInvoiceNumber, refreshInvoiceNumber, loading: invoiceLoading } = useInvoice();
  const {
    state: printerState,
    printInvoice: sendPrintJob,
    enqueueJob: enqueuePrintJob,
  } = usePrinterService();
  
  // State
  const [billName, setBillName] = useState('');
  const [billItems, setBillItems] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [moneyAccounts, setMoneyAccounts] = useState([]);
  const [org, setOrg] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', price: '', unit: 'piece' });
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '' });
  const [selectedMoneyAccountId, setSelectedMoneyAccountId] = useState('');
  const [printing, setPrinting] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percent'
  const [cashReceived, setCashReceived] = useState('');
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const isNetworkError = (error) => {
    if (!error) return false;
    const message = error.message || error.toString();
    return message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('request') && message.includes('failed');
  };

  // Refresh bill number from context
  const refreshBillNumber = () => {
    if (currentInvoiceNumber) {
      setBillName(currentInvoiceNumber);
    }
  };


  // Update bill name when invoice number changes
  useEffect(() => {
    if (currentInvoiceNumber && !billName && !isEditMode) {
      setBillName(currentInvoiceNumber);
    }
  }, [currentInvoiceNumber, billName, isEditMode]);

  // Load order data when editing
  useEffect(() => {
    if (orderToEdit?.orderId) {
      const loadOrderData = async () => {
        try {
          setIsEditMode(true);
          setEditingOrderId(orderToEdit.orderId);
          setBillName(orderToEdit.name || '');
          
          // Load items with proper structure
          const items = (orderToEdit.items || []).map((item, idx) => ({
            ...item,
            id: item.id || Date.now() + idx + Math.random(),
            discountPercent: item.discountPercent || 0
          }));
          setBillItems(items);
          
          // Set discount
          if (orderToEdit.discountPercent) {
            setDiscount(orderToEdit.discountPercent);
            setDiscountType('percent');
          } else if (orderToEdit.discount) {
            setDiscount(orderToEdit.discount);
            setDiscountType('amount');
          }
          
          // Set money account
          if (orderToEdit.money_account_id) {
            setSelectedMoneyAccountId(orderToEdit.money_account_id);
          }

          if (Object.prototype.hasOwnProperty.call(orderToEdit, 'cashReceived')) {
            setCashReceived(
              orderToEdit.cashReceived === undefined || orderToEdit.cashReceived === null
                ? ''
                : String(orderToEdit.cashReceived)
            );
          } else {
            setCashReceived('');
          }
          
          // Find and set customer
          if (orderToEdit.customerId) {
            const customer = customers.find(c => c.customerId === orderToEdit.customerId);
            if (customer) {
              setSelectedCustomer(customer);
              setCustomerSearchTerm(customer.name);
            } else {
              // Customer might not be loaded yet, try to find it
              try {
                const customerData = await customersApi.list();
                const foundCustomer = customerData.find(c => c.customerId === orderToEdit.customerId);
                if (foundCustomer) {
                  setSelectedCustomer(foundCustomer);
                  setCustomerSearchTerm(foundCustomer.name);
                }
              } catch (err) {
                console.error('Failed to load customer for edit', err);
              }
            }
          }
        } catch (error) {
          console.error('Failed to load order data for editing', error);
          showToast({ message: 'Failed to load order data', variant: 'error' });
        }
      };
      
      loadOrderData();
    } else {
      setIsEditMode(false);
      setEditingOrderId(null);
    }
  }, [orderToEdit, customers]);

  const {
    data: offlineCustomers,
    loading: customersLoading,
    refresh: refreshCustomers
  } = useOfflineCollection({
    entityType: ENTITY_TYPES.CUSTOMER,
    fetcher: () => customersApi.list(),
    orgId: user?.orgId
  });

  const {
    data: offlineProducts,
    loading: productsLoading,
    refresh: refreshProducts
  } = useOfflineCollection({
    entityType: ENTITY_TYPES.PRODUCT,
    fetcher: () => productApi.list(),
    orgId: user?.orgId
  });

  const {
    data: offlineMoneyAccounts,
    loading: accountsLoading,
    refresh: refreshAccounts
  } = useOfflineCollection({
    entityType: ENTITY_TYPES.MONEY_ACCOUNT,
    fetcher: () => accountsApi.list(),
    orgId: user?.orgId
  });

  const loading = customersLoading || productsLoading || accountsLoading || invoiceLoading;

  useEffect(() => {
    setCustomers(Array.isArray(offlineCustomers) ? offlineCustomers : []);
  }, [offlineCustomers]);

  useEffect(() => {
    setProducts(Array.isArray(offlineProducts) ? offlineProducts : []);
  }, [offlineProducts]);

  useEffect(() => {
    setMoneyAccounts(Array.isArray(offlineMoneyAccounts) ? offlineMoneyAccounts : []);
  }, [offlineMoneyAccounts]);

  useEffect(() => {
    let cancelled = false;

    const loadOrgProfile = async () => {
      try {
        const cached = await dataService.getMetadata('org_profile');
        if (!cancelled && cached) {
          setOrg(cached);
          return;
        }
      } catch (error) {
        console.error('Failed to load cached org profile', error);
      }

      if (!cancelled) {
        setOrg({
          name: 'Your Business',
          address: 'Your Address',
          phone: 'Your Phone',
          email: 'your@email.com'
        });
      }
    };

    loadOrgProfile();

    return () => {
      cancelled = true;
    };
  }, [user?.orgId]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowCustomerDropdown(false);
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter customers
  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    (customer.phone && customer.phone.toLowerCase().includes(customerSearchTerm.toLowerCase()))
  );

  // Filter products
  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  // Add product to bill
  const addProductToBill = (product) => {
    const productId = product.productId || product.id;
    
    // Check if product already exists in the bill
    const existingItemIndex = billItems.findIndex(item => item.productId === productId);
    
    if (existingItemIndex !== -1) {
      // Product exists, increase quantity
      setBillItems(prevItems => 
        prevItems.map((item, index) => 
          index === existingItemIndex 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      // Product doesn't exist, add as new item
      const newItem = {
        productId: productId,
        name: product.name,
        price: parseFloat(product.price),
        quantity: 1,
        unit: product.unit,
        discountPercent: product.discountPercent || 0, // Load discount from product
        id: Date.now() + Math.random() // Unique ID for each bill item
      };
      
      setBillItems(prevItems => [...prevItems, newItem]);
    }
  };

  // Update quantity
  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      setBillItems(prevItems => prevItems.filter(item => item.id !== itemId));
    } else {
      setBillItems(prevItems => prevItems.map(item =>
        item.id === itemId
          ? { ...item, quantity: parseFloat(quantity) }
          : item
      ));
    }
  };

  // Update price
  const updatePrice = (itemId, price) => {
    setBillItems(prevItems => prevItems.map(item =>
      item.id === itemId
        ? { ...item, price: parseFloat(price) || 0 }
        : item
    ));
  };

  // Update discount percent for an item
  const updateItemDiscount = (itemId, discountPercent) => {
    setBillItems(prevItems => prevItems.map(item =>
      item.id === itemId
        ? { ...item, discountPercent: parseFloat(discountPercent) || 0 }
        : item
    ));
  };

  // Remove item
  const removeItem = (itemId) => {
    setBillItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  // Calculate item total with discount (only if overall discount is 0)
  const calculateItemTotal = (item) => {
    const baseTotal = item.price * item.quantity;
    // Only apply item-level discount if overall discount is 0
    if (discount === 0 && item.discountPercent) {
      const itemDiscount = (baseTotal * item.discountPercent) / 100;
      return baseTotal - itemDiscount;
    }
    return baseTotal;
  };

  // Calculate subtotal (with item-level discounts only if overall discount is 0)
  const calculateSubtotal = () => {
    return billItems.reduce((total, item) => total + calculateItemTotal(item), 0);
  };

  // Calculate discount amount
  const calculateDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    if (discountType === 'percent') {
      return (subtotal * discount) / 100;
    }
    return discount || 0;
  };

  // Calculate total
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscountAmount();
    return Math.max(0, subtotal - discountAmount);
  };

  const buildPrintableDraftInvoice = (metadataOverrides = {}, paymentOverrides = {}) => {
    const businessInfo = extractBusinessFromOrg(org || {});
    const printableItems = billItems.map((item) => {
      const itemTotal = calculateItemTotal(item);
      const baseTotal = item.price * item.quantity;
      const itemDiscount = item.discountPercent ? (baseTotal * item.discountPercent) / 100 : 0;
      return {
        id: item.productId || item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        unit: item.unit,
        discountPercent: item.discountPercent || 0,
        discount: itemDiscount,
        lineTotal: itemTotal,
        originalTotal: baseTotal,
      };
    });

    const customerInfo = selectedCustomer
      ? {
          id: selectedCustomer.customerId,
          name: selectedCustomer.name,
          phone: selectedCustomer.phone,
          email: selectedCustomer.email,
          address: selectedCustomer.address,
          customerNumber: selectedCustomer.customerNumber,
        }
      : {};

    const subtotal = calculateSubtotal();
    const resolvedCashAmount = cashReceived === '' ? undefined : Math.max(0, Number(cashReceived) || 0);
    const discountAmount = calculateDiscountAmount();
    return buildPrintableInvoiceFromDraft({
      billName,
      billItems: printableItems,
      customer: customerInfo,
      business: businessInfo,
      totals: {
        subTotal: subtotal,
        discount: discountAmount,
        grandTotal: calculateTotal(),
      },
      payment: {
        ...paymentOverrides,
        method: resolvedCashAmount && resolvedCashAmount > 0 ? 'Cash' : (paymentOverrides?.method || ''),
        amount: resolvedCashAmount ?? paymentOverrides?.amount ?? 0,
      },
      metadata: {
        accountId: selectedAccountId || null,
        moneyAccountId: selectedMoneyAccountId || null,
        createdBy: user?.name || user?.displayName || user?.email || null,
        ...metadataOverrides,
      },
    });
  };

  const printerConnected = printerState?.connected;
  const printerName = printerState?.device?.name || printerState?.persisted?.device?.name;
  const printerQueueCount = Array.isArray(printerState?.queue) ? printerState.queue.length : 0;
  const remoteBridge = printerState?.remoteHost || {};
  const remoteBridgeStatus = remoteBridge.status || (remoteBridge.supported ? 'idle' : 'unsupported');

  const resetBillForm = () => {
    const nextBillNumber = generateNextInvoiceNumber();
    setBillItems([]);
    setSelectedCustomer(null);
    setCustomerSearchTerm('');
    setBillName(nextBillNumber);
    setDiscount(0);
    setDiscountType('amount');
    setCashReceived('');
    setIsEditMode(false);
    setEditingOrderId(null);
    return nextBillNumber;
  };

  const buildOrderPayload = () => {
    if (!selectedCustomer) return null;

    const payload: any = {
      ...(isEditMode && editingOrderId ? { orderId: editingOrderId } : {}),
      customerId: selectedCustomer.customerId,
      customerName: selectedCustomer.name,
      customerPhone: selectedCustomer.phone,
      customerAddress: selectedCustomer.address || '',
      customerEmail: selectedCustomer.email || '',
      customerNumber: selectedCustomer.customerNumber || '',
      date: isEditMode && orderToEdit?.date ? orderToEdit.date : new Date().toISOString().split('T')[0],
      items: billItems.map(item => ({
        productId: item.productId,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        unit: item.unit,
        discountPercent: item.discountPercent || 0
      })),
      sellingPrice: calculateTotal(),
      discount: calculateDiscountAmount(),
      discountPercent: discountType === 'percent' ? discount : 0,
      totalCost: 0,
      source: 'orders',
      billing: true,
      money_account_id: selectedMoneyAccountId || null,
      name: billName,
      notes: `Invoice: ${billName}`,
      orgId: user?.orgId || null,
      accountId: selectedAccountId || null
    };

    if (cashReceived !== '') {
      payload.cashReceived = Math.max(0, Number(cashReceived) || 0);
    }

    return payload;
  };

  const submitOrder = async (orderData) => {
    const isUpdate = isEditMode && editingOrderId;
    
    if (!navigator.onLine) {
      if (isUpdate) {
        await syncService.queueUpdate(ENTITY_TYPES.ORDER, editingOrderId, orderData);
      } else {
        await syncService.queueCreate(ENTITY_TYPES.ORDER, orderData, { orgId: user?.orgId });
      }
      return { queued: true };
    }

    try {
      let result;
      if (isUpdate) {
        const { orderId, ...updates } = orderData;
        result = await ordersApi.update(orderId, updates);
        if (result) {
          const updatedOrder = { ...orderToEdit, ...updates, orderId };
          await dataService.upsertFromServer(ENTITY_TYPES.ORDER, [updatedOrder], { orgId: user?.orgId });
        }
        return { updatedOrder: result };
      } else {
        result = await ordersApi.create(orderData);
        if (result) {
          await dataService.upsertFromServer(ENTITY_TYPES.ORDER, [result], { orgId: user?.orgId });
        }
        return { createdOrder: result };
      }
    } catch (error) {
      if (isNetworkError(error)) {
        if (isUpdate) {
          await syncService.queueUpdate(ENTITY_TYPES.ORDER, editingOrderId, orderData);
        } else {
          await syncService.queueCreate(ENTITY_TYPES.ORDER, orderData, { orgId: user?.orgId });
        }
        return { queued: true, error };
      }
      throw error;
    }
  };

  // Create bill
  const createBill = async () => {
    if (!billName.trim()) {
      return;
    }

    if (!selectedCustomer) {
      return;
    }

    if (!canPerformOperation) {
      showToast({
        message: offlineMessage || 'Offline billing is not available. Please connect to the internet.',
        variant: 'warning',
      });
      return;
    }

    try {
      const orderData = buildOrderPayload();
      if (!orderData) return;


      const result = await submitOrder(orderData);

      // Generate next invoice number for the next bill
      const nextBillNumber = generateNextInvoiceNumber();

      // Reset form after creating and generate next invoice number
      if (!isEditMode) {
        setBillItems([]);
        setSelectedCustomer(null);
        setCustomerSearchTerm('');
        
        // Update bill name with next invoice number immediately
        setBillName(nextBillNumber);
      }

      if (result?.queued) {
        showToast({
          message: isEditMode ? 'Bill update saved offline. It will sync automatically when you are back online.' : 'Bill saved offline. It will sync automatically when you are back online.',
          variant: 'success',
        });
      } else {
        showToast({
          message: isEditMode ? 'Bill updated successfully.' : 'Bill created successfully.',
          variant: 'success',
        });
      }

      // Call onEditComplete callback if provided
      if (isEditMode && onEditComplete) {
        onEditComplete();
      }
    } catch (error) {
      console.error('Error in createBill:', error);
      showToast({
        message: 'Failed to create bill. Please try again.',
        variant: 'error',
      });
    }
  };

  // Share bill with link only (better WhatsApp preview)
  const shareBill = async () => {
    if (!selectedCustomer || !selectedCustomer.phone) {
      return;
    }

    if (!canPerformOperation) {
      showToast({
        message: offlineMessage || 'Offline billing is not available. Please connect to the internet.',
        variant: 'warning',
      });
      return;
    }

    try {
      const orderData = buildOrderPayload();
      if (!orderData) return;


      const result = await submitOrder(orderData);

      if (result?.queued) {
        showToast({
          message: 'Bill saved offline. Share once you are back online from the Orders screen.',
          variant: 'success',
        });
        const nextBillNumber = generateNextInvoiceNumber();
        setBillItems([]);
        setSelectedCustomer(null);
        setCustomerSearchTerm('');
        setBillName(nextBillNumber);
        return;
      }

      const order = result?.createdOrder || result?.updatedOrder || (isEditMode ? { ...orderToEdit, ...orderData } : null);
      const printableInvoice = order
        ? buildPrintableInvoiceFromOrder(order, org, {
            metadata: {
              source: 'fast-billing',
              extras: { trigger: 'fastBilling.shareBill' },
            },
          })
        : buildPrintableDraftInvoice({
            extras: { trigger: 'fastBilling.shareBill' },
          });
      const invoiceData = printableInvoiceToLegacyInvoiceData(printableInvoice);

      // Send invoice via WhatsApp with link (no image for better preview)
      await invoiceImageService.sendInvoiceViaWhatsApp(
        selectedCustomer.phone, 
        invoiceData, 
        org,
        { includeImage: false } // Send only link for better WhatsApp preview
      );

      // Call onEditComplete callback if provided
      if (isEditMode && onEditComplete) {
        onEditComplete();
      }

      // Increment invoice number after order creation and WhatsApp sharing
      if (!isEditMode) {
        const nextBillNumber = generateNextInvoiceNumber();

        // Reset form after sharing (regardless of WhatsApp success)
        setBillItems([]);
        setSelectedCustomer(null);
        setCustomerSearchTerm('');
        
        // Update bill name with next invoice number immediately
        setBillName(nextBillNumber);
      }
      
      showToast({
        message: isEditMode ? 'Invoice updated and shared successfully.' : 'Invoice shared successfully.',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error in shareBill:', error);
      showToast({
        message: 'Failed to share invoice. Please try again.',
        variant: 'error',
      });
    }
  };

  // Share bill with image attachment
  const shareBillWithImage = async () => {
    if (!selectedCustomer || !selectedCustomer.phone) {
      return;
    }

    if (!canPerformOperation) {
      showToast({
        message: offlineMessage || 'Offline billing is not available. Please connect to the internet.',
        variant: 'warning',
      });
      return;
    }

    try {
      const orderData = buildOrderPayload();
      if (!orderData) return;


      const result = await submitOrder(orderData);

      if (result?.queued) {
        showToast({
          message: 'Bill saved offline. Share with image once you are back online from the Orders screen.',
          variant: 'success',
        });
        const nextBillNumber = generateNextInvoiceNumber();
        setBillItems([]);
        setSelectedCustomer(null);
        setCustomerSearchTerm('');
        setBillName(nextBillNumber);
        return;
      }

      const order = result?.createdOrder || result?.updatedOrder || (isEditMode ? { ...orderToEdit, ...orderData } : null);
      const printableInvoice = order
        ? buildPrintableInvoiceFromOrder(order, org, {
            metadata: {
              source: 'fast-billing',
              extras: { trigger: 'fastBilling.shareBillWithImage' },
            },
          })
        : buildPrintableDraftInvoice({
            extras: { trigger: 'fastBilling.shareBillWithImage' },
          });
      const invoiceData = printableInvoiceToLegacyInvoiceData(printableInvoice);

      // Send invoice via WhatsApp with image attachment
      await invoiceImageService.sendInvoiceViaWhatsApp(
        selectedCustomer.phone, 
        invoiceData, 
        org,
        { includeImage: true } // Send with image attachment
      );

      // Call onEditComplete callback if provided
      if (isEditMode && onEditComplete) {
        onEditComplete();
      }

      // Increment invoice number after order creation and WhatsApp sharing
      if (!isEditMode) {
        const nextBillNumber = generateNextInvoiceNumber();

        // Reset form after sharing (regardless of WhatsApp success)
        setBillItems([]);
        setSelectedCustomer(null);
        setCustomerSearchTerm('');
        
        // Update bill name with next invoice number immediately
        setBillName(nextBillNumber);
      }
      
      showToast({
        message: isEditMode ? 'Invoice updated and shared with image successfully.' : 'Invoice shared with image successfully.',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error in shareBillWithImage:', error);
      showToast({
        message: 'Failed to share invoice with image. Please try again.',
        variant: 'error',
        duration: 7000,
      });
    }
  };

  const printBill = async () => {
    if (printing) return;
    if (!billName.trim()) {
      showToast({ message: 'Please enter a bill name before printing.', variant: 'warning' });
      return;
    }
    if (!selectedCustomer) {
      showToast({ message: 'Select a customer before printing.', variant: 'warning' });
      return;
    }
    if (billItems.length === 0) {
      showToast({ message: 'Add at least one product before printing.', variant: 'warning' });
      return;
    }
    if (!canPerformOperation) {
      showToast({
        message: offlineMessage || 'Offline billing is not available. Please connect to the internet.',
        variant: 'warning',
      });
      return;
    }

    setPrinting(true);
    let printableDoc = null;
    let shouldResetForm = false;

    try {
      const orderData = buildOrderPayload();
      if (!orderData) {
        showToast({ message: 'Unable to build order data.', variant: 'error' });
        return;
      }

      const result = await submitOrder(orderData);
      const order = result?.createdOrder || result?.updatedOrder || (isEditMode ? { ...orderToEdit, ...orderData } : null);
      shouldResetForm = !isEditMode;

      if (result?.queued) {
        showToast({
          message: isEditMode ? 'Bill update saved offline. It will sync automatically when you reconnect.' : 'Bill saved offline. It will sync automatically when you reconnect.',
          variant: 'success',
        });
        printableDoc = buildPrintableDraftInvoice({
          extras: { trigger: 'fastBilling.print', queuedOrder: true },
        });
      } else {
        showToast({
          message: isEditMode ? 'Bill updated. Sending to printer…' : 'Bill created. Sending to printer…',
          variant: 'success',
        });
        printableDoc = order
          ? buildPrintableInvoiceFromOrder(order, org, {
              metadata: {
                source: 'fast-billing',
                extras: { trigger: 'fastBilling.print' },
              },
            })
          : buildPrintableDraftInvoice({
              extras: { trigger: 'fastBilling.print', missingOrder: true },
            });
      }

      // Call onEditComplete callback if provided
      if (isEditMode && onEditComplete) {
        onEditComplete();
      }

      const printResult = await sendPrintJob(printableDoc, {
        allowQueue: true,
        encoder: { autoCut: false },
      });

      if (printResult?.remote) {
        showToast({
          message: 'Print job sent to desktop printer.',
          variant: 'success',
        });
      } else if (printResult?.queued) {
        showToast({
          message: printResult?.remoteFallback
            ? 'Desktop printer is offline. Print job saved to local queue.'
            : 'Printer not connected. Print job saved to printer queue.',
          variant: printResult?.remoteFallback ? 'warning' : 'warning',
        });
      } else {
        showToast({
          message: 'Print job sent to thermal printer.',
          variant: 'success',
        });
      }
    } catch (error) {
      console.error('Failed to print bill', error);
      if (error?.code === 'REMOTE_PRINT_FAILED') {
        showToast({
          message: error.message || 'Desktop printer is unavailable right now.',
          variant: 'error',
        });
      }
      const fallbackDoc =
        printableDoc ||
        buildPrintableDraftInvoice({
          extras: { trigger: 'fastBilling.print', error: true },
        });
      try {
        await enqueuePrintJob(fallbackDoc, { reason: 'print-error' });
        showToast({
          message: 'Printer error. Job saved for retry in printer queue.',
          variant: 'warning',
        });
      } catch (queueError) {
        console.error('Failed to queue print job', queueError);
        showToast({
          message: 'Failed to print or queue. Check printer setup.',
          variant: 'error',
        });
      }
    } finally {
      setPrinting(false);
      if (shouldResetForm) {
        resetBillForm();
      }
    }
  };

  // Add new customer
  const addNewCustomer = async () => {
    if (!newCustomer.name.trim()) {
      return;
    }

    if (!newCustomer.phone.trim()) {
      return;
    }

    try {
      const customerData = {
        name: newCustomer.name.trim(),
        phone: newCustomer.phone.trim(),
        email: '',
        address: ''
      };

      if (!navigator.onLine) {
        const localRecord = await syncService.queueCreate(ENTITY_TYPES.CUSTOMER, customerData, { orgId: user?.orgId });
        const recordData = localRecord.data || customerData;
        setCustomers(prev => [...prev, recordData]);
        setSelectedCustomer(recordData);
        setCustomerSearchTerm(recordData.name);
        showToast({
          message: 'Customer saved offline. It will sync when you reconnect.',
          variant: 'success',
        });
      } else {
        try {
          const createdCustomer = await customersApi.create(customerData);
          setCustomers(prev => [...prev, createdCustomer]);
          await dataService.upsertFromServer(ENTITY_TYPES.CUSTOMER, [createdCustomer], { orgId: user?.orgId });
          await refreshCustomers({ force: true });
          setSelectedCustomer(createdCustomer);
          setCustomerSearchTerm(createdCustomer.name);
        } catch (error) {
          if (isNetworkError(error)) {
            const localRecord = await syncService.queueCreate(ENTITY_TYPES.CUSTOMER, customerData, { orgId: user?.orgId });
            const recordData = localRecord.data || customerData;
            setCustomers(prev => [...prev, recordData]);
            setSelectedCustomer(recordData);
            setCustomerSearchTerm(recordData.name);
            showToast({
              message: 'Customer saved offline. It will sync when you reconnect.',
              variant: 'success',
            });
          } else {
            throw error;
          }
        }
      }

      setShowAddCustomer(false);
      setNewCustomer({ name: '', phone: '' });
    } catch (error) {
      console.error('Failed to add customer', error);
    }
  };

  // Add new product
  const addNewProduct = async () => {
    if (!newProduct.name.trim()) {
      return;
    }

    if (!newProduct.price || parseFloat(newProduct.price) <= 0) {
      return;
    }

    try {
      const productData = {
        name: newProduct.name.trim(),
        price: parseFloat(newProduct.price),
        unit: newProduct.unit,
        description: ''
      };

      if (!navigator.onLine) {
        const localRecord = await syncService.queueCreate(ENTITY_TYPES.PRODUCT, productData, { orgId: user?.orgId });
        const recordData = localRecord.data || productData;
        setProducts(prev => [...prev, recordData]);
        showToast({
          message: 'Product saved offline. It will sync when you reconnect.',
          variant: 'success',
        });
      } else {
        try {
          const createdProduct = await productApi.create(productData);
          setProducts(prev => [...prev, createdProduct]);
          await dataService.upsertFromServer(ENTITY_TYPES.PRODUCT, [createdProduct], { orgId: user?.orgId });
          await refreshProducts({ force: true });
        } catch (error) {
          if (isNetworkError(error)) {
            const localRecord = await syncService.queueCreate(ENTITY_TYPES.PRODUCT, productData, { orgId: user?.orgId });
            const recordData = localRecord.data || productData;
            setProducts(prev => [...prev, recordData]);
            showToast({
              message: 'Product saved offline. It will sync when you reconnect.',
              variant: 'success',
            });
          } else {
            throw error;
          }
        }
      }
      setShowAddProduct(false);
      setNewProduct({ name: '', price: '', unit: 'piece' });
    } catch (error) {
      console.error('Failed to add product', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header - Matches SideNav Theme */}
      <div className="bg-white border-b border-green-100 px-4 py-3">
        <h1 className="text-lg font-semibold text-gray-800">
          {isEditMode ? '✏️ Edit Bill' : '⚡ Fast Billing'}
        </h1>
        <p className="text-sm text-gray-600">
          {isEditMode ? 'Edit invoice details' : 'Quick invoice generation and WhatsApp sharing'}
        </p>
      </div>

      {/* Offline Warning */}
      {showOfflineWarning && (
        <div className="mx-4 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">{offlineMessage}</p>
        </div>
      )}

      <div className="p-4 space-y-3">
        {/* Mobile-First Form - Matches App Theme */}
        <div className="space-y-3">
          {/* Bill Name */}
          <div className="bg-white rounded-lg border border-green-100 p-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Bill Name</label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={billName}
                onChange={(e) => setBillName(e.target.value)}
                placeholder="Invoice #001"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                onClick={refreshBillNumber}
                className="px-3 py-2 text-sm bg-green-100 text-green-700 hover:bg-green-200 border border-green-300 rounded-lg transition-all duration-200 flex items-center space-x-1"
                title="Refresh bill number"
              >
                <span className="text-sm">🔄</span>
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          {/* Customer Selection */}
          <div className="bg-white rounded-lg border border-green-100 p-3">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Customer</label>
              <button
                onClick={() => setShowAddCustomer(true)}
                className="text-sm text-green-600 hover:text-green-800 font-medium"
              >
                + Add New
              </button>
            </div>
            <div className="relative dropdown-container">
              <input
                type="text"
                value={customerSearchTerm}
                onChange={(e) => {
                  setCustomerSearchTerm(e.target.value);
                  setShowCustomerDropdown(true);
                }}
                onFocus={() => setShowCustomerDropdown(true)}
                placeholder="Search customer..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              {showCustomerDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowAddCustomer(true);
                        setShowCustomerDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg border-2 border-dashed border-green-300 text-sm"
                    >
                      + Add New Customer
                    </button>
                  </div>
                  {filteredCustomers.map(customer => (
                    <button
                      key={customer.customerId}
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setCustomerSearchTerm(customer.name);
                        setShowCustomerDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-green-50 border-b border-gray-100 last:border-b-0 text-sm"
                    >
                      <div className="font-medium">{customer.name}</div>
                      <div className="text-xs text-gray-500">{customer.phone}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Selection */}
          <div className="bg-white rounded-lg border border-green-100 p-3">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Add Product</label>
              <PermissionAction actionKey="products:create">
                <button
                  onClick={() => setShowAddProduct(true)}
                  className="text-sm text-green-600 hover:text-green-800 font-medium"
                >
                  + Add New
                </button>
              </PermissionAction>
            </div>
            <div className="relative dropdown-container">
              <input
                type="text"
                value={productSearchTerm}
                onChange={(e) => {
                  setProductSearchTerm(e.target.value);
                  setShowProductDropdown(true);
                }}
                onFocus={() => setShowProductDropdown(true)}
                placeholder="Search product..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              {showProductDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowAddProduct(true);
                        setShowProductDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg border-2 border-dashed border-green-300 text-sm"
                    >
                      + Add New Product
                    </button>
                  </div>
                  {filteredProducts.map(product => (
                    <button
                      key={product.id}
                      onClick={() => {
                        addProductToBill(product);
                        setProductSearchTerm('');
                        setShowProductDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-green-50 border-b border-gray-100 last:border-b-0 text-sm"
                    >
                      <div className="font-medium">{product.name}</div>
                      <div className="text-xs text-gray-500">₹{product.price} per {product.unit}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Money Account Selection */}
          <div className="bg-white rounded-lg border border-green-100 p-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Money Account</label>
            <select
              value={selectedMoneyAccountId}
              onChange={(e) => setSelectedMoneyAccountId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Select Money Account (Optional)</option>
              {moneyAccounts.map(account => (
                <option key={account.accountId} value={account.accountId}>
                  {account.name} - ₹{account.balance?.toFixed(2) || '0.00'}
                </option>
              ))}
            </select>
          </div>

          {/* Discount Section */}
          <div className="bg-white rounded-lg border border-green-100 p-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Overall Discount</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={discountType}
                onChange={(e) => {
                  setDiscountType(e.target.value);
                  setDiscount(0);
                }}
                className="w-full sm:w-auto sm:min-w-[140px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="amount">Amount (₹)</option>
                <option value="percent">Percent (%)</option>
              </select>
              <input
                type="number"
                value={discount}
                onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                className="w-full sm:flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder={discountType === 'percent' ? '0' : '0.00'}
                step={discountType === 'percent' ? '1' : '0.01'}
                min="0"
              />
            </div>
            {discount > 0 && (
              <div className="mt-2 text-xs text-green-600">
                Discount: ₹{calculateDiscountAmount().toFixed(2)}
              </div>
            )}
            {discount > 0 && (
              <div className="mt-1 text-xs text-gray-500">
                Note: Overall discount overwrites item-level discounts
              </div>
            )}
          </div>

          {/* Cash Received Section */}
          {billItems.length > 0 && (
            <div className="bg-white rounded-lg border border-green-100 p-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Cash Received</label>
              <input
                type="number"
                value={cashReceived}
                onChange={(e) => setCashReceived(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Leave blank to auto-fill full amount"
                step="0.01"
                min="0"
              />
              <div className="text-[11px] text-gray-500 mt-1">
                Leave blank if the full bill amount was collected.
              </div>
              {cashReceived !== '' && (
                (() => {
                  const totalAmount = calculateTotal();
                  const cashValue = Math.max(0, Number(cashReceived) || 0);
                  return (
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Total Amount:</span>
                        <span className="font-semibold text-gray-800">₹{totalAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Cash Received:</span>
                        <span className="font-semibold text-green-600">₹{cashValue.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs pt-1 border-t border-green-200">
                        <span className="text-gray-700 font-medium">Change to Give:</span>
                        <span className={`font-bold ${cashValue >= totalAmount ? 'text-green-600' : 'text-red-600'}`}>
                          ₹{Math.max(0, cashValue - totalAmount).toFixed(2)}
                        </span>
                      </div>
                      {cashValue < totalAmount && (
                        <div className="text-xs text-red-600 mt-1">
                          ⚠️ Short by ₹{(totalAmount - cashValue).toFixed(2)}
                        </div>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </div>

        {/* Bill Items - Mobile-Friendly List */}
        {billItems.length > 0 && (
          <div className="bg-white rounded-lg border border-green-100">
            {/* Header with Total and Top Action Buttons */}
            <div className="p-3 border-b border-green-100 bg-green-50">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-800">Bill Items ({billItems.length})</h2>
                <div className="text-right">
                  {calculateSubtotal() !== calculateTotal() && (
                    <div className="text-xs text-gray-500 line-through mb-1">
                      ₹{calculateSubtotal().toFixed(2)}
                    </div>
                  )}
                  <div className="text-lg font-bold text-green-600">₹{calculateTotal().toFixed(2)}</div>
                  {discount > 0 && (
                    <div className="text-xs text-green-600 mt-1">
                      -₹{calculateDiscountAmount().toFixed(2)} discount
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between text-xs text-gray-500 mb-3 gap-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span>🖨️ Printer:</span>
                    {printerConnected ? (
                      <span className="text-green-700 font-semibold flex items-center gap-1">
                        {printerState?.device?.transport === 'bluetooth' ? '📶' : 
                         printerState?.device?.transport === 'usb' ? '🔌' : ''}
                        {printerName || 'Connected'}
                      </span>
                    ) : (
                      <span className="text-gray-500">Not connected</span>
                    )}
                  </div>
                  {remoteBridge.supported && printerState?.environment === 'electron' && (
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <span>Desktop bridge:</span>
                      <span
                        className={
                          remoteBridgeStatus === 'listening'
                            ? 'text-green-600 font-semibold'
                            : remoteBridgeStatus === 'printing'
                              ? 'text-blue-600 font-semibold'
                              : remoteBridgeStatus === 'error'
                                ? 'text-red-600 font-semibold'
                                : 'text-gray-500'
                        }
                      >
                        {remoteBridgeStatus === 'listening' && 'Listening'}
                        {remoteBridgeStatus === 'printing' && 'Printing'}
                        {remoteBridgeStatus === 'registering' && 'Registering'}
                        {remoteBridgeStatus === 'idle' && 'Idle'}
                        {remoteBridgeStatus === 'error' && 'Needs attention'}
                      </span>
                    </div>
                  )}
                </div>
                {printerQueueCount > 0 && (
                  <div className="text-yellow-700 font-semibold">
                    {printerQueueCount} job(s) queued
                  </div>
                )}
              </div>
              
              {/* Top Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={createBill}
                  disabled={!billName.trim() || printing}
                  className="bg-green-600 text-white py-2 px-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-xs shadow-sm hover:shadow-md border border-green-700"
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span className="text-sm">💾</span>
                    <span>{printing ? 'Processing…' : 'Create Bill'}</span>
                  </div>
                </button>
                
                <button
                  onClick={shareBill}
                  disabled={!selectedCustomer || !selectedCustomer.phone || printing}
                  className="bg-white text-green-600 py-2 px-3 rounded-lg hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-xs shadow-sm hover:shadow-md border-2 border-green-600 hover:border-green-700"
                >
                  <div className="flex items-center justify-center space-x-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                    </svg>
                    <span>Send Invoice</span>
                  </div>
                </button>
                <button
                  onClick={printBill}
                  disabled={!billName.trim() || !selectedCustomer || billItems.length === 0 || printing}
                  className="bg-white text-green-600 py-2 px-3 rounded-lg hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-xs shadow-sm hover:shadow-md border border-green-300"
                  title="Create bill and send to thermal printer"
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span className="text-sm">🖨️</span>
                    <span>{printing ? 'Printing…' : 'Create & Print'}</span>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Mobile-Optimized Item List - White & Green Theme */}
            <div className="p-3 space-y-3">
              {billItems.map((item, index) => (
                <div key={item.id} className="bg-white border border-green-200 rounded-lg p-3 shadow-sm">
                  {/* Product Name and Remove Button */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm truncate">{item.name}</div>
                      <div className="text-xs text-green-600">per {item.unit}</div>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-500 hover:text-red-700 text-lg ml-2 flex-shrink-0"
                    >
                      ✕
                    </button>
                  </div>
                  
                  {/* Controls Row - Mobile Optimized */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {/* Quantity */}
                    <div className="min-w-0">
                      <label className="block text-xs font-medium text-green-700 mb-1">Qty</label>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1))}
                          className="w-6 h-6 rounded-full bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center text-xs font-bold"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.id, parseFloat(e.target.value) || 0)}
                          className="w-12 text-center border border-green-300 rounded px-1 py-1 text-xs font-medium focus:ring-1 focus:ring-green-500 focus:border-green-500"
                          min="0"
                          step="0.01"
                        />
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-6 h-6 rounded-full bg-green-100 text-green-600 hover:bg-green-200 flex items-center justify-center text-xs font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    {/* Price */}
                    <div className="min-w-0">
                      <label className="block text-xs font-medium text-green-700 mb-1">Price</label>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => updatePrice(item.id, e.target.value)}
                        className="w-full border border-green-300 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-green-500 focus:border-green-500"
                        step="0.01"
                        min="0"
                        placeholder="0"
                      />
                    </div>

                    {/* Discount */}
                    <div className="min-w-0">
                      <label className="block text-xs font-medium text-green-700 mb-1">Disc. %</label>
                      <input
                        type="number"
                        value={item.discountPercent || 0}
                        onChange={(e) => updateItemDiscount(item.id, e.target.value)}
                        className="w-full border border-green-300 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-green-500 focus:border-green-500"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="0"
                      />
                    </div>
                    
                    {/* Total */}
                    <div className="min-w-0">
                      <label className="block text-xs font-medium text-green-700 mb-1">Total</label>
                      <div className="text-xs font-bold text-green-600 bg-green-50 border border-green-200 rounded px-1 py-1 text-center truncate">
                        ₹{calculateItemTotal(item).toFixed(2)}
                      </div>
                      {item.discountPercent > 0 && discount === 0 && (
                        <div className="text-[10px] text-gray-500 line-through mt-0.5">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Action Buttons - Theme Matched */}
            <div className="p-4 border-t border-green-100 bg-green-50">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={createBill}
                  disabled={!billName.trim() || printing}
                  className="bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-sm shadow-sm hover:shadow-md border border-green-700"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-lg">💾</span>
                    <span>{printing ? 'Processing…' : (isEditMode ? 'Update Bill' : 'Create Bill')}</span>
                  </div>
                </button>
                
                <button
                  onClick={shareBill}
                  disabled={!selectedCustomer || !selectedCustomer.phone || printing}
                  className="bg-white text-green-600 py-3 px-4 rounded-lg hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-sm shadow-sm hover:shadow-md border-2 border-green-600 hover:border-green-700"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                    </svg>
                    <span>Send Invoice</span>
                  </div>
                </button>
                <button
                  onClick={printBill}
                  disabled={!billName.trim() || !selectedCustomer || billItems.length === 0 || printing}
                  className="bg-white text-green-600 py-3 px-4 rounded-lg hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-sm shadow-sm hover:shadow-md border border-green-300"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-lg">🖨️</span>
                    <span>{printing ? 'Printing…' : 'Create & Print'}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No Items Message */}
        {billItems.length === 0 && (
          <div className="bg-white rounded-lg border border-green-100 p-6 text-center">
            <div className="text-gray-500 mb-4">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Start Creating Your Bill</h3>
            <p className="text-gray-600 mb-4">Add products to create your invoice</p>
            <div className="text-sm text-gray-500">
              <p>• Select a customer from the dropdown</p>
              <p>• Search and add products</p>
              <p>• Create bill or send invoice via WhatsApp</p>
            </div>
          </div>
        )}

        {/* Add Customer Modal */}
        {showAddCustomer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Add New Customer</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Phone number"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddCustomer(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={addNewCustomer}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Add Customer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Product Modal */}
        {showAddProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Add New Product</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Product name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                  <input
                    type="number"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Price"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <select
                    value={newProduct.unit}
                    onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="piece">Piece</option>
                    <option value="kg">Kg</option>
                    <option value="liter">Liter</option>
                    <option value="meter">Meter</option>
                    <option value="box">Box</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddProduct(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={addNewProduct}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Add Product
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FastBilling;